import { useState, useEffect } from 'react';
import {
  Typography, Descriptions, Tag, Spin, Avatar, Space, Button, message,
  Modal, Select, Input,
} from 'antd';
import {
  UserOutlined, MailOutlined, SafetyCertificateOutlined, CalendarOutlined, LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { userApi } from '@/api/userApi';
import { roleRequestApi } from '@/api/roleRequestApi';
import { useAuth } from '@/auth/useAuth';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import PageHeader from '@/components/ui/PageHeader';
import type { User, UserRole } from '@/types/common';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO',
  BD_MANAGER: 'BD Manager',
  ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect',
  OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager',
  TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead',
  CONTRIBUTOR: 'Contributor',
  ADMIN: 'Administrator',
};

const ROLE_COLORS: Record<UserRole, string> = {
  CEO: 'gold',
  BD_MANAGER: 'blue',
  ENTERPRISE_ARCHITECT: 'purple',
  CLOUD_ARCHITECT: 'cyan',
  OPPORTUNITY_FINDER: 'green',
  REQUIREMENT_RESOURCING_MGR: 'orange',
  TECHNICAL_WRITER: 'magenta',
  QC_LEAD: 'red',
  CONTRIBUTOR: 'default',
  ADMIN: 'volcano',
};

const ALL_ROLES: UserRole[] = [
  'CEO', 'BD_MANAGER', 'ENTERPRISE_ARCHITECT', 'CLOUD_ARCHITECT',
  'OPPORTUNITY_FINDER', 'REQUIREMENT_RESOURCING_MGR', 'TECHNICAL_WRITER', 'QC_LEAD', 'ADMIN',
];

const ProfilePage = () => {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [roleRequestOpen, setRoleRequestOpen] = useState(false);
  const [requestedRole, setRequestedRole] = useState<UserRole | ''>('');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    userApi.getCurrentUser()
      .then(setProfile)
      .catch(() => message.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmitRoleRequest = async () => {
    if (!requestedRole) return;
    setSubmitting(true);
    try {
      const res = await roleRequestApi.submit({
        requestedRole: requestedRole as UserRole,
        reason: requestReason || undefined,
      });
      message.success(res.data.message || 'Role request submitted');
      setRoleRequestOpen(false);
      setRequestedRole('');
      setRequestReason('');
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to submit role request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Text type="secondary">Unable to load profile information.</Text>
      </div>
    );
  }

  const initials = profile.fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Exclude roles the user already holds + CEO cannot be requested
  const excludedRoles = new Set<UserRole>(['CEO', profile.role]);
  if (profile.secondaryRole) excludedRoles.add(profile.secondaryRole);
  const availableRoles = ALL_ROLES.filter((r) => !excludedRoles.has(r));
  const canRequestRole = availableRoles.length > 0;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <PageHeader
        title="Profile"
        subtitle="Your account details and access"
      />

      <div
        className="ui-surface ui-animate-in"
        style={{
          padding: 28,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <Avatar
          size={72}
          style={{
            background: 'var(--gradient-brand)',
            fontSize: 28,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <div style={{ minWidth: 0 }}>
          <Title level={3} style={{ margin: 0, letterSpacing: '-0.02em' }}>{profile.fullName}</Title>
          <Space size={8} style={{ marginTop: 6 }} wrap>
            <Tag color={ROLE_COLORS[profile.role]}>{ROLE_LABELS[profile.role] ?? profile.role}</Tag>
            {profile.secondaryRole && (
              <Tag color={ROLE_COLORS[profile.secondaryRole]}>
                {ROLE_LABELS[profile.secondaryRole] ?? profile.secondaryRole} (Secondary)
              </Tag>
            )}
            <Tag color={profile.isActive ? 'green' : 'default'}>
              {profile.isActive ? 'Active' : 'Inactive'}
            </Tag>
          </Space>
        </div>
      </div>

      <div
        className="ui-surface ui-animate-in"
        style={{ padding: 28, marginBottom: 16, animationDelay: '0.06s' }}
      >
        <Text
          strong
          style={{
            display: 'block',
            marginBottom: 16,
            fontSize: 13,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Account Details
        </Text>
        <Descriptions column={1} labelStyle={{ fontWeight: 500, width: 160 }} bordered size="middle">
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 6 }} />Full Name</>}>
            {profile.fullName}
          </Descriptions.Item>
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 6 }} />Username</>}>
            {authUser?.preferred_username || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={<><MailOutlined style={{ marginRight: 6 }} />Email</>}>
            {profile.email}
          </Descriptions.Item>
          <Descriptions.Item label={<><SafetyCertificateOutlined style={{ marginRight: 6 }} />Primary Role</>}>
            <Tag color={ROLE_COLORS[profile.role]}>{ROLE_LABELS[profile.role] ?? profile.role}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={<><SafetyCertificateOutlined style={{ marginRight: 6 }} />Secondary Role</>}>
            {profile.secondaryRole ? (
              <Tag color={ROLE_COLORS[profile.secondaryRole]}>
                {ROLE_LABELS[profile.secondaryRole] ?? profile.secondaryRole}
              </Tag>
            ) : (
              <Text type="secondary">None</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Member Since</>}>
            {profile.createdAt ? dayjs(profile.createdAt).format('MMMM D, YYYY') : '—'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button
            icon={<LockOutlined />}
            onClick={() => setChangePasswordOpen(true)}
          >
            Change Password
          </Button>
          {canRequestRole && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => setRoleRequestOpen(true)}
            >
              Request Additional Role
            </Button>
          )}
        </div>
      </div>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      {/* Role Request Modal */}
      <Modal
        title="Request Additional Role"
        open={roleRequestOpen}
        onOk={handleSubmitRoleRequest}
        onCancel={() => { setRoleRequestOpen(false); setRequestedRole(''); setRequestReason(''); }}
        okText="Submit Request"
        okButtonProps={{ disabled: !requestedRole, loading: submitting }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Text>
            Request a secondary role to gain additional permissions. An admin or CEO will review your request.
          </Text>
          <div>
            <Text strong>Select Role:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choose a role..."
              value={requestedRole || undefined}
              onChange={(v) => setRequestedRole(v as UserRole)}
            >
              {availableRoles.map((r) => (
                <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong>Reason (optional):</Text>
            <Input.TextArea
              style={{ marginTop: 8 }}
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder="Explain why you need this additional role..."
              rows={3}
              maxLength={500}
              showCount
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default ProfilePage;
